import { Check } from 'lucide-react';
import type { ValidationStage } from '../api/hooks.js';

// 3-stage day workflow indicator (mockup .workflow): Saisie → Revue →
// Validation, driven by the day-level validationStage.
//
// The labels used to be hardcoded first names — 'Saisie · Bouchra',
// 'Revue · Bouchra', 'Validation · Chouaib'. Bouchra appeared twice,
// Lamis not at all, and the daily flow assigned no actors: whoever held
// attendance.edit could fill a day in AND validate it alone. The names
// now come from who actually acted on this day (resolved server-side on
// the roster rows) and a step with no known actor simply carries no name
// rather than naming the wrong person.
const LABELS = ['Saisie', 'Revue', 'Validation'] as const;

export interface WorkflowActors {
  /** Who filled the day in and signed the first level. */
  saisie?: string | null;
  /** Who is reviewing / reviewed it — known once the day is closed. */
  revue?: string | null;
  /** Who closed the day — only known once it is validated. */
  validation?: string | null;
}

// The three steps now map to the two real gates of the day:
//   Saisie      — DRAFT/READY: being filled, arbitrated, not yet signed.
//   Revue       — VALIDATED_L1: the preparer signed; it is the reviewer's turn.
//   Validation  — VALIDATED: the reviewer signed; the day is closed.
// Before Lot 2 the middle step had no gate and no actor, so READY was
// mapped straight to Validation to keep step 3 reachable. It has both now.
export function currentIndexFor(stage: ValidationStage): number {
  if (stage === 'VALIDATED') return 3; // all done
  if (stage === 'VALIDATED_L1') return 1;
  return 0; // DRAFT / READY — still being filled in
}

export function WorkflowIndicator({ stage, actors }: {
  stage: ValidationStage;
  actors?: WorkflowActors;
}) {
  const currentIndex = currentIndexFor(stage);
  const names: Array<string | null | undefined> = [actors?.saisie, actors?.revue, actors?.validation];
  return (
    <div className="workflow" data-testid="workflow-indicator" data-stage={stage}>
      {LABELS.map((label, i) => {
        const done = i < currentIndex;
        const current = i === currentIndex;
        const cls = done ? 'wf-step done' : current ? 'wf-step current' : 'wf-step';
        const name = names[i];
        return (
          <span key={label} style={{ display: 'inline-flex', alignItems: 'center' }}>
            {i > 0 && <span className="wf-sep" />}
            <span className={cls}>
              <span className="wf-dot">
                {done && <Check size={7} aria-hidden="true" />}
              </span>
              {name ? `${label} · ${name}` : label}
            </span>
          </span>
        );
      })}
    </div>
  );
}
